import { tt } from '@agos/shared'

import { normaliseHost } from '@agos/customer-core'

/**
 * The branches a visitor may switch between, and what the gate does with them.
 *
 * `GET /api/v1/central/branches` is **host-resolved** — `?host=`, then `X-Franchise-Host`, then the
 * `Host` header. Measured on the AGOS backend:
 *
 * | host asked | `currentId` | branches |
 * |---|---|---|
 * | `pizzamajstro.sk` | 1 | Šurany, Nesvady |
 * | `nesvady.pizzamajstro.sk` | 7 | the same two |
 * | `localhost:18080` | `null` | **none**, `group: null` |
 *
 * That last row is the one to know before somebody debugs it: on a development origin the endpoint
 * correctly answers *no branches*, because no `franchise_domains` row matches. It is not a failure and
 * the picker must not present it as one — with no branches there is nothing to choose, and the app
 * simply reveals itself.
 *
 * **Franchise 8 has no domain row at all** and is correctly absent. So the switcher never offers a
 * branch that cannot be opened — which is the right behaviour and worth a test, because the obvious
 * implementation (list every active franchise) would offer it and then navigate to `https:///`.
 */

export interface Branch {
  id: number
  name: string
  slug: string
  primaryDomain: string
}

export interface BranchRegistry {
  branches: Branch[]
  group: string | null
  currentId: number | null
  /**
   * Whether the branch this visitor is on is soft-launched - `BACKEND.md` §10A.131.
   *
   * **Read so that the preference is not pinned to it.** The cookie is scoped to the brand's common
   * parent domain, so a visitor who dismissed the picker on a soft-launched branch would be
   * `redirect`ed back to it **from the main site**, months later, by a cookie nobody remembers
   * setting. Legacy carries the same field for the same stated reason.
   */
  hiddenBranch: boolean
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function registryOf(payload: unknown): BranchRegistry {
  const row = (payload ?? {}) as Record<string, unknown>
  const rows = Array.isArray(row.branches) ? row.branches : []
  return {
    group: text(row.group) === '' ? null : text(row.group),
    currentId: typeof row.currentId === 'number' ? row.currentId : null,
    // Absent reads as false: a server that has not been told about soft launches has no hidden
    // branches, and defaulting the other way would stop every visitor pinning a preference.
    hiddenBranch: row.hiddenBranch === true,
    branches: rows.flatMap((entry) => {
      const branch = entry as Record<string, unknown>
      const domain = normaliseHost(text(branch.primaryDomain))
      // A branch with no reachable domain is not a branch this switcher can offer.
      if (typeof branch.id !== 'number' || domain === '') return []
      return [
        {
          id: branch.id,
          name: text(branch.name) || String(branch.id),
          slug: text(branch.slug),
          primaryDomain: domain,
        },
      ]
    }),
  }
}

export function currentBranch(registry: BranchRegistry): Branch | null {
  return registry.branches.find((branch) => branch.id === registry.currentId) ?? null
}

// `otherBranches` was here and is deleted — §9.73, owner decision 2026-08-06. It filtered the current
// branch out of the list; the picker lists every branch and marks the one you are on, and the owner
// was offered the swap and kept what is there. So this was a second opinion on a settled question,
// and a reader finding both would not know which the screen follows.

/** Every domain the brand answers on — what `commonParentDomain` needs to scope the cookie. */
export function domainsOf(registry: BranchRegistry): string[] {
  return registry.branches.map((branch) => branch.primaryDomain)
}

/**
 * What the gate should do, decided in one place rather than across three effects.
 *
 * - `reveal` — nothing to choose or already in the right place; drop the splash and show the app.
 * - `ask` — a first-time visitor with a real choice; show the picker over the splash.
 * - `redirect` — a stored preference for somewhere else.
 *
 * The `fkb=` marker is why `redirect` is not simply *pref !== host*: it is the flag the inline script
 * sets on the URL it navigates to, and without honouring it two branches that each think they are not
 * the preferred one bounce forever.
 */
export type GateAction =
  | { kind: 'reveal' }
  | { kind: 'ask' }
  | { kind: 'redirect'; to: string }

export function gateAction(
  registry: BranchRegistry,
  pref: string | null,
  host: string,
  hasMarker: boolean,
): GateAction {
  const here = normaliseHost(host)

  if (pref !== null) {
    if (pref === here || hasMarker) return { kind: 'reveal' }
    // **Never off-site from a development origin.** The preference names a production domain, and on
    // `localhost` the tenant comes from the cookie the preference is stored in — so there is nothing to
    // navigate to and the app simply reveals. Without this, opening the local app after choosing a
    // branch lands on the live site, which is the second half of the same defect `branchUrl` had.
    if (isLocalHost(here)) return { kind: 'reveal' }
    // Only to a branch this brand actually has. A stale cookie naming a domain that is no longer a
    // branch would otherwise send somebody to a host nobody serves.
    const known = registry.branches.some((branch) => branch.primaryDomain === pref)
    return known ? { kind: 'redirect', to: `https://${pref}/?fkb=1` } : { kind: 'ask' }
  }

  // Nothing stored. One branch — or none, which is what a development origin answers — is not a choice.
  return registry.branches.length > 1 ? { kind: 'ask' } : { kind: 'reveal' }
}

/** Said above the picker on a first visit, where there is no *cancel*. */
export function welcomeNote(registry: BranchRegistry): string {
  return registry.group === null
    ? tt('Vyberte si pobočku, z ktorej chcete objednávať.')
    : `${registry.group} - ${tt('vyberte si pobočku, z ktorej chcete objednávať.')}`
}

/** Said when somebody reopens the switcher, where there is. */
export function switchNote(current: Branch | null): string {
  return current === null
    ? tt('Prepnúť pobočku')
    : `${tt('Objednávate z')} ${current.name}. ${tt('Prepnutím sa košík nezoberie so sebou - je to iná prevádzka s iným menu.')}`
}

/**
 * Where choosing a branch sends the customer.
 *
 * In production each branch **is** a domain — that is how one deployment serves `pizzamajstro.sk` and
 * `nesvady.pizzamajstro.sk` as different restaurants, and the backend resolves the tenant from the
 * `Host` header. So switching means navigating there.
 *
 * **Locally there is one origin and the tenant comes from the `fk_branch` cookie**, which
 * `setBranchPref` has just written. Navigating to `https://pizzamajstro.sk` at that point leaves the
 * machine entirely and lands on the live site — which is what happened, and it makes the branch picker
 * untestable on a development stack. On a host that is not a real branch domain the switch reloads **in
 * place** and the dev proxy picks the new branch up from the cookie.
 *
 * `currentHost` is passed in rather than read here so this stays a pure function the tests can drive.
 */
export function branchUrl(branch: Branch, currentHost: string): string {
  return isLocalHost(currentHost) ? '/?fkb=1' : `https://${branch.primaryDomain}/?fkb=1`
}

/** A development origin: no dots, or an explicit loopback. Every real branch domain has a dot in it. */
export function isLocalHost(host: string): boolean {
  const bare = normaliseHost(host)
  return bare === 'localhost' || bare === '127.0.0.1' || bare === '[::1]' || !bare.includes('.')
}

/**
 * Whether a preference may be pinned from this branch - §10A.131.
 *
 * **The one place the soft-launch flag changes the customer app.** `BranchPicker.dismiss` pins the
 * current host on the reasoning that *"whoever is here is on a branch; pin it"* - which is right for
 * a branch that is offered and wrong for one that is not: the cookie lives on the shared parent, so
 * a soft-launched branch that pinned itself would later pull that visitor off the main site.
 */
export function mayPinPreference(registry: BranchRegistry): boolean {
  return !registry.hiddenBranch
}
